import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import type { Vector2 } from '../core/constants';

export class Kamikaze implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2;
  active = true;

  directionX: number;
  directionY: number;
  chargeSpeed = 400;
  maxDistance = 300;
  distanceTraveled = 0;

  explosionRadius = 55;
  maxDamage = 55;

  sourceWormId: string;
  ownerTeamId: string;

  onExplode?: (x: number, y: number) => void;
  onHitWorm?: (targetId: string) => void;
  onDrill?: (x: number, y: number) => void;

  constructor(
    x: number,
    y: number,
    dirX: number,
    dirY: number,
    sourceWormId: string,
    teamId: string,
  ) {
    this.id = generateEntityId('kamikaze');
    this.position = { x, y };
    this.directionX = dirX;
    this.directionY = dirY;
    this.velocity = { x: dirX * this.chargeSpeed, y: dirY * this.chargeSpeed };
    this.sourceWormId = sourceWormId;
    this.ownerTeamId = teamId;
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    const moveX = this.velocity.x * deltaTime;
    const moveY = this.velocity.y * deltaTime;

    this.position.x += moveX;
    this.position.y += moveY;
    this.distanceTraveled += Math.sqrt(moveX * moveX + moveY * moveY);

    if (this.distanceTraveled >= this.maxDistance) {
      this.explode();
    }
  }

  checkTerrainAndDrill(terrain: { isSolid(x: number, y: number): boolean }): void {
    const probeX = this.position.x + this.directionX * 10;
    const probeY = this.position.y + this.directionY * 10;

    if (terrain.isSolid(probeX, probeY)) {
      this.onDrill?.(this.position.x, this.position.y);
    }
  }

  checkWormCollision(
    worms: Array<{
      id: string;
      position: Vector2;
      state: string;
      getBounds(): { x: number; y: number; width: number; height: number };
    }>,
  ): string | null {
    for (const worm of worms) {
      if (worm.id === this.sourceWormId) continue;
      if (worm.state === 'dead') continue;

      const bounds = worm.getBounds();
      const dx = this.position.x - (bounds.x + bounds.width / 2);
      const dy = this.position.y - (bounds.y + bounds.height / 2);
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 25) {
        return worm.id;
      }
    }
    return null;
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  destroy(): void {
    this.active = false;
  }
}

export const WeaponKamikaze: WeaponDefinition = {
  id: 'kamikaze',
  name: 'Kamikaze',
  icon: 'kamikaze.1.png',
  category: 'melee',
  maxAmmo: 1,
  maxUsesPerTurn: 1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle } = ctx;

    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const dirX = Math.cos(aimRad);
    const dirY = -Math.sin(aimRad);

    worm.kamikazing = true;
    worm.kamikazeDirection = { x: dirX, y: dirY };
    worm.kamikazeDistance = 0;
    worm.skipGraveOnDeath = true;
    worm.isGrounded = false;

    return {
      success: true,
      entities: [],
      message: 'BANZAI!',
    };
  },
};
